/**
 * Data Domains (EDC-style) domain model.
 *
 * A "data domain" is a semantic classification for a column — IBAN, Email,
 * Account Number, etc. Domains are organised into groups (Bank Account,
 * Transaction, …) and matched against real column schemas by the classifier
 * in ./classify.ts. Mirrors the Informatica EDC data-domain concept.
 */

export type DomainSource = 'auto' | 'manual';
export type AssignmentStatus = 'suggested' | 'accepted' | 'rejected';

export interface DomainGroup {
  id: string;
  name: string;
  /** Tailwind accent used for the group's badges/bars. */
  accent: 'brand' | 'sky' | 'violet' | 'amber' | 'crimson';
}

export interface DataDomain {
  id: string;
  name: string;
  groupId: string;
  description: string;
  /** Column-name keywords / synonyms the classifier matches against. */
  keywords: string[];
  /** When true, a numeric column type boosts confidence (e.g. Balance). */
  numeric?: boolean;
  /** User-defined domains carry this flag and live in localStorage. */
  custom?: boolean;
}

/** A column's resolved domain state (suggestion overlaid with any decision). */
export interface DomainAssignment {
  key: string; // `${datasetId}::${column}`
  datasetId: string;
  datasetName: string;
  column: string;
  dataType: string;
  domainId: string | null;
  confidence: number; // 0–100
  status: AssignmentStatus;
  source: DomainSource;
}

export const DOMAIN_GROUPS: readonly DomainGroup[] = [
  { id: 'identity', name: 'Customer Identity & Contact', accent: 'brand' },
  { id: 'account', name: 'Bank Account', accent: 'sky' },
  { id: 'transaction', name: 'Transaction', accent: 'violet' },
  { id: 'geography', name: 'Geography', accent: 'amber' },
  { id: 'compliance', name: 'Risk & Compliance', accent: 'crimson' },
] as const;

/**
 * Packaged banking domain library. Modelled on the 60+ domains Informatica EDC
 * ships, tuned for the Al Wadi core-banking demo (customers / accounts /
 * transactions).
 */
export const PACKAGED_DOMAINS: readonly DataDomain[] = [
  // --- Customer Identity & Contact ---
  { id: 'customer_id', name: 'Customer ID', groupId: 'identity', description: 'Unique customer identifier.', keywords: ['customer_id', 'customerid', 'cust_id', 'custid', 'customer_no', 'customer_number', 'client_id'] },
  { id: 'national_id', name: 'National ID / Emirates ID', groupId: 'identity', description: 'Government identity number (Emirates ID, passport, SSN).', keywords: ['national_id', 'nationalid', 'emirates_id', 'emiratesid', 'passport', 'passport_no', 'ssn', 'nid', 'id_number'] },
  { id: 'full_name', name: 'Full Name', groupId: 'identity', description: 'Person or customer name.', keywords: ['name', 'full_name', 'fullname', 'customer_name', 'first_name', 'last_name', 'surname'] },
  { id: 'email', name: 'Email Address', groupId: 'identity', description: 'Electronic mail address.', keywords: ['email', 'email_address', 'e_mail', 'mail'] },
  { id: 'phone', name: 'Phone Number', groupId: 'identity', description: 'Telephone or mobile number.', keywords: ['phone', 'phone_number', 'mobile', 'telephone', 'contact_number', 'msisdn'] },
  { id: 'dob', name: 'Date of Birth', groupId: 'identity', description: 'Customer date of birth.', keywords: ['dob', 'date_of_birth', 'birth_date', 'birthdate'] },
  { id: 'gender', name: 'Gender', groupId: 'identity', description: 'Gender / sex.', keywords: ['gender', 'sex'] },

  // --- Bank Account ---
  { id: 'account_number', name: 'Account Number', groupId: 'account', description: 'Bank account identifier.', keywords: ['account_number', 'account_no', 'accountnumber', 'acct_no', 'acct_number', 'account_id', 'accountid', 'acct'] },
  { id: 'iban', name: 'IBAN', groupId: 'account', description: 'International Bank Account Number.', keywords: ['iban'] },
  { id: 'account_type', name: 'Account Type', groupId: 'account', description: 'Product / account category (savings, current…).', keywords: ['account_type', 'accounttype', 'acct_type', 'product_type', 'product'] },
  { id: 'account_status', name: 'Account Status', groupId: 'account', description: 'Account lifecycle status.', keywords: ['account_status', 'acct_status', 'status'] },
  { id: 'balance', name: 'Balance', groupId: 'account', description: 'Monetary account balance.', keywords: ['balance', 'current_balance', 'available_balance', 'avail_balance', 'ledger_balance'], numeric: true },
  { id: 'currency', name: 'Currency', groupId: 'account', description: 'ISO currency code.', keywords: ['currency', 'ccy', 'curr', 'currency_code'] },
  { id: 'branch', name: 'Branch Code', groupId: 'account', description: 'Branch / sort code.', keywords: ['branch', 'branch_code', 'branch_id', 'sort_code', 'swift'] },
  { id: 'open_date', name: 'Account Open Date', groupId: 'account', description: 'Date the account was opened.', keywords: ['open_date', 'opening_date', 'opened', 'date_opened', 'created_at'] },

  // --- Transaction ---
  { id: 'transaction_id', name: 'Transaction ID', groupId: 'transaction', description: 'Unique transaction identifier.', keywords: ['transaction_id', 'transactionid', 'txn_id', 'txnid', 'trans_id', 'reference', 'ref_no'] },
  { id: 'txn_amount', name: 'Transaction Amount', groupId: 'transaction', description: 'Monetary value of a transaction.', keywords: ['amount', 'transaction_amount', 'txn_amount', 'amt', 'value', 'debit', 'credit'], numeric: true },
  { id: 'txn_type', name: 'Transaction Type', groupId: 'transaction', description: 'Debit/credit or transaction category.', keywords: ['transaction_type', 'txn_type', 'trans_type', 'type', 'dr_cr', 'debit_credit'] },
  { id: 'txn_date', name: 'Transaction Date', groupId: 'transaction', description: 'Date/time the transaction posted.', keywords: ['transaction_date', 'txn_date', 'trans_date', 'date', 'posted_date', 'timestamp', 'value_date'] },
  { id: 'merchant', name: 'Merchant / Counterparty', groupId: 'transaction', description: 'Merchant, payee or counterparty.', keywords: ['merchant', 'counterparty', 'payee', 'beneficiary', 'vendor', 'description'] },
  { id: 'channel', name: 'Channel', groupId: 'transaction', description: 'Origination channel (ATM, online…).', keywords: ['channel', 'source', 'medium'] },

  // --- Geography ---
  { id: 'city', name: 'City', groupId: 'geography', description: 'City / town.', keywords: ['city', 'town'] },
  { id: 'region', name: 'Region / Province', groupId: 'geography', description: 'Region, province, state or emirate.', keywords: ['region', 'province', 'state', 'emirate'] },
  { id: 'country', name: 'Country', groupId: 'geography', description: 'Country or country code.', keywords: ['country', 'nation', 'country_code'] },
  { id: 'address', name: 'Address', groupId: 'geography', description: 'Street / postal address.', keywords: ['address', 'street', 'addr', 'address_line'] },
  { id: 'postal', name: 'Postal Code', groupId: 'geography', description: 'ZIP / postal code.', keywords: ['postal_code', 'postcode', 'zip', 'zipcode', 'po_box'] },

  // --- Risk & Compliance ---
  { id: 'kyc', name: 'KYC Status', groupId: 'compliance', description: 'Know-Your-Customer verification status.', keywords: ['kyc', 'kyc_status', 'verification_status'] },
  { id: 'risk', name: 'Risk Rating', groupId: 'compliance', description: 'Customer / account risk rating.', keywords: ['risk', 'risk_rating', 'risk_level', 'risk_score'] },
  { id: 'credit_score', name: 'Credit Score', groupId: 'compliance', description: 'Creditworthiness score.', keywords: ['credit_score', 'creditscore', 'score', 'fico'], numeric: true },
  { id: 'pep', name: 'PEP Flag', groupId: 'compliance', description: 'Politically Exposed Person flag.', keywords: ['pep', 'politically_exposed'] },
] as const;

export function accentClasses(accent: DomainGroup['accent']): string {
  const map: Record<DomainGroup['accent'], string> = {
    brand: 'bg-brand-50 text-brand-700 ring-brand-200 dark:bg-brand-500/15 dark:text-brand-300 dark:ring-brand-500/30',
    sky: 'bg-accent-sky/10 text-accent-sky ring-accent-sky/30',
    violet: 'bg-accent-violet/10 text-accent-violet ring-accent-violet/30',
    amber: 'bg-accent-amber/10 text-accent-amber ring-accent-amber/30',
    crimson: 'bg-accent-crimson/10 text-accent-crimson ring-accent-crimson/30',
  };
  return map[accent];
}
